import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Save, Trash2, Plus, ArrowLeft, 
  User, GraduationCap, Briefcase, FolderOpen, Wrench,
  Eye, EyeOff, Loader2, BarChart3, Users, MousePointer,
  Mail, Linkedin, FileText, Award, Link, Edit3
} from 'lucide-react';
import {
  adminLogin,
  verifyAdmin,
  getAllContent,
  getVisitorStats,
  updateAbout,
  saveEducation,
  deleteEducation,
  saveExperience,
  deleteExperience,
  saveProject,
  deleteProject,
  saveSkill,
  deleteSkill,
  saveCertification,
  deleteCertification
} from '../services/api';

// Login Component
function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await adminLogin(email, password);
      if (result.success) {
        localStorage.setItem('adminToken', result.token);
        onLogin(result.token);
      } else {
        setError(result.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-8">
          <h1 className="text-2xl font-heading font-bold text-white mb-2 text-center">Admin Login</h1>
          <p className="text-[#aaa] text-sm text-center mb-6">Sign in to manage portfolio content</p>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-2 rounded mb-4 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-mono text-[#aaa] mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded px-4 py-2 text-white focus:border-[#12d640] focus:outline-none transition-colors"
                placeholder="admin@example.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-mono text-[#aaa] mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded px-4 py-2 pr-10 text-white focus:border-[#12d640] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded font-mono text-sm uppercase tracking-wider font-medium text-black disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-[#12d640] text-sm hover:underline flex items-center justify-center gap-2">
              <ArrowLeft size={14} /> Back to Portfolio
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Stats Dashboard Component
function StatsDashboard({ stats }) {
  const statCards = [
    { label: 'Total Visits', value: stats?.total_visits || 0, icon: Users, color: '#12d640' },
    { label: 'Unique Visitors', value: stats?.unique_visitors || 0, icon: User, color: '#00d4ff' },
    { label: 'Page Views', value: stats?.page_views || 0, icon: Eye, color: '#f59e0b' },
    { label: 'Contact Clicks', value: stats?.contact_clicks || 0, icon: MousePointer, color: '#ec4899' },
    { label: 'LinkedIn Clicks', value: stats?.linkedin_clicks || 0, icon: Linkedin, color: '#3b82f6' },
    { label: 'Email Clicks', value: stats?.email_clicks || 0, icon: Mail, color: '#8b5cf6' },
    { label: 'Resume Downloads', value: stats?.resume_downloads || 0, icon: FileText, color: '#ef4444' },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-heading text-white flex items-center gap-2">
        <BarChart3 size={24} className="text-[#12d640]" /> Analytics Dashboard
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="glass-card p-4 text-center"
          >
            <stat.icon size={24} className="mx-auto mb-2" style={{ color: stat.color }} />
            <p className="text-2xl font-heading font-bold text-white">{stat.value}</p>
            <p className="text-xs font-mono text-[#666] uppercase">{stat.label}</p>
          </motion.div>
        ))}
      </div>
      <p className="text-xs text-[#666] font-mono">
        Stats since: {stats?.created_at ? new Date(stats.created_at).toLocaleDateString() : 'N/A'}
      </p>
    </div>
  );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-xs sm:text-sm transition-all ${
        active 
          ? 'bg-[#12d640] text-black' 
          : 'text-[#aaa] hover:text-white hover:bg-[#1a1a1a]'
      }`}
      data-testid={`tab-${label.toLowerCase()}`}
    >
      <Icon size={16} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// About Editor
function AboutEditor({ data, token, onSave }) {
  const [text, setText] = useState(data?.text || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(data?.text || '');
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAbout(token, { text });
      onSave();
      alert('About section saved!');
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-heading text-white flex items-center gap-2">
        <Edit3 size={20} className="text-[#12d640]" /> Edit About Section
      </h2>
      <p className="text-sm text-[#666]">Use double line breaks to create paragraphs.</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full h-64 bg-[#1a1a1a] border border-[#333] rounded p-4 text-white focus:border-[#12d640] focus:outline-none resize-none text-sm"
        placeholder="Enter your about text here..."
        data-testid="about-textarea"
      />
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2 rounded font-mono text-sm text-black"
        style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
        data-testid="save-about-btn"
      >
        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
        Save Changes
      </button>
    </div>
  );
}

// Education Editor
function EducationEditor({ data, token, onSave }) {
  const [items, setItems] = useState(data || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    institution: '', degree: '', period: '', location: '', gpa: '', courses: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleAdd = () => {
    setEditingId('new');
    setForm({ institution: '', degree: '', period: '', location: '', gpa: '', courses: '' });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      institution: item.institution || '',
      degree: item.degree || '',
      period: item.period || '',
      location: item.location || '',
      gpa: item.gpa || '',
      courses: item.courses || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: editingId === 'new' ? undefined : editingId
      };
      await saveEducation(token, payload);
      setEditingId(null);
      onSave();
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this education entry?')) return;
    try {
      await deleteEducation(token, id);
      onSave();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading text-white">Education</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#12d640] text-[#12d640] hover:bg-[#12d640]/10 font-mono text-sm"
          data-testid="add-education-btn"
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {editingId && (
        <div className="glass-card p-4 space-y-3">
          <input
            placeholder="Institution *"
            value={form.institution}
            onChange={(e) => setForm({...form, institution: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Degree *"
            value={form.degree}
            onChange={(e) => setForm({...form, degree: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              placeholder="Period (e.g., Jan 2022 - Dec 2023)"
              value={form.period}
              onChange={(e) => setForm({...form, period: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
            <input
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm({...form, location: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
          </div>
          <input
            placeholder="GPA (optional)"
            value={form.gpa}
            onChange={(e) => setForm({...form, gpa: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Courses (comma separated)"
            value={form.courses}
            onChange={(e) => setForm({...form, courses: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.institution || !form.degree}
              className="flex items-center gap-2 px-4 py-2 rounded text-black text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded border border-[#333] text-[#aaa] text-sm hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-4 flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate">{item.institution}</h3>
              <p className="text-[#aaa] text-sm truncate">{item.degree}</p>
              <p className="text-[#666] text-xs">{item.period} • {item.location}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-2 text-[#aaa] hover:text-[#12d640]">
                <Edit3 size={16} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-[#aaa] hover:text-red-500">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[#666] text-sm text-center py-8">No education entries. Click "Add" to create one.</p>
        )}
      </div>
    </div>
  );
}

// Experience Editor
function ExperienceEditor({ data, token, onSave }) {
  const [items, setItems] = useState(data || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    company: '', title: '', period: '', location: '', type: '', description: '', tools: '', linkedin_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleAdd = () => {
    setEditingId('new');
    setForm({ company: '', title: '', period: '', location: '', type: 'Full-time', description: '', tools: '', linkedin_url: '' });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      company: item.company || '',
      title: item.title || '',
      period: item.period || '',
      location: item.location || '',
      type: item.type || 'Full-time',
      description: item.description || '',
      tools: item.tools || '',
      linkedin_url: item.linkedin_url || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: editingId === 'new' ? undefined : editingId
      };
      await saveExperience(token, payload);
      setEditingId(null);
      onSave();
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this experience entry?')) return;
    try {
      await deleteExperience(token, id);
      onSave();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading text-white">Work Experience</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#12d640] text-[#12d640] hover:bg-[#12d640]/10 font-mono text-sm"
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {editingId && (
        <div className="glass-card p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              placeholder="Company *"
              value={form.company}
              onChange={(e) => setForm({...form, company: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
            <input
              placeholder="Job Title *"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              placeholder="Period"
              value={form.period}
              onChange={(e) => setForm({...form, period: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
            <input
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm({...form, location: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            />
            <select
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value})}
              className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
            >
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Internship">Internship</option>
              <option value="Contract">Contract</option>
              <option value="Freelance">Freelance</option>
            </select>
          </div>
          <textarea
            placeholder="Description (one bullet point per line)"
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            className="w-full h-32 bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white resize-none text-sm"
          />
          <input
            placeholder="Tools & Technologies used (comma separated)"
            value={form.tools}
            onChange={(e) => setForm({...form, tools: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="LinkedIn URL (optional)"
            value={form.linkedin_url}
            onChange={(e) => setForm({...form, linkedin_url: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.company || !form.title}
              className="flex items-center gap-2 px-4 py-2 rounded text-black text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded border border-[#333] text-[#aaa] text-sm hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-4 flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate">{item.company}</h3>
              <p className="text-[#aaa] text-sm truncate">{item.title}</p>
              <p className="text-[#666] text-xs">{item.period} • {item.type}</p>
              {item.tools && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.tools.split(',').slice(0, 5).map((tool, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-[#12d640]/10 text-[#12d640] rounded">{tool.trim()}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-2 text-[#aaa] hover:text-[#12d640]">
                <Edit3 size={16} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-[#aaa] hover:text-red-500">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[#666] text-sm text-center py-8">No experience entries. Click "Add" to create one.</p>
        )}
      </div>
    </div>
  );
}

// Projects Editor
function ProjectsEditor({ data, token, onSave }) {
  const [items, setItems] = useState(data || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', link: '', tags: '', tools: '', linkedin_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleAdd = () => {
    setEditingId('new');
    setForm({ title: '', description: '', link: '', tags: '', tools: '', linkedin_url: '' });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      description: item.description || '',
      link: item.link || '',
      tags: item.tags || '',
      tools: item.tools || '',
      linkedin_url: item.linkedin_url || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: editingId === 'new' ? undefined : editingId
      };
      await saveProject(token, payload);
      setEditingId(null);
      onSave();
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await deleteProject(token, id);
      onSave();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading text-white">Projects</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#12d640] text-[#12d640] hover:bg-[#12d640]/10 font-mono text-sm"
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {editingId && (
        <div className="glass-card p-4 space-y-3">
          <input
            placeholder="Project Title *"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <textarea
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            className="w-full h-24 bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white resize-none text-sm"
          />
          <input
            placeholder="Project URL"
            value={form.link}
            onChange={(e) => setForm({...form, link: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Tags (comma separated, e.g., Python, MATLAB)"
            value={form.tags}
            onChange={(e) => setForm({...form, tags: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Tools & Software used (comma separated)"
            value={form.tools}
            onChange={(e) => setForm({...form, tools: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="LinkedIn Project URL (optional)"
            value={form.linkedin_url}
            onChange={(e) => setForm({...form, linkedin_url: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.title}
              className="flex items-center gap-2 px-4 py-2 rounded text-black text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded border border-[#333] text-[#aaa] text-sm hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-4 flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate">{item.title}</h3>
              <p className="text-[#aaa] text-sm line-clamp-2">{item.description}</p>
              {item.link && (
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-[#12d640] text-xs flex items-center gap-1 mt-1">
                  <Link size={12} /> View
                </a>
              )}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-1.5 text-[#aaa] hover:text-[#12d640]">
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-1.5 text-[#aaa] hover:text-red-500">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[#666] text-sm text-center py-8 col-span-2">No projects. Click "Add" to create one.</p>
        )}
      </div>
    </div>
  );
}

// Skills Editor
function SkillsEditor({ data, token, onSave }) {
  const [items, setItems] = useState(data || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: '', skills: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleAdd = () => {
    setEditingId('new');
    setForm({ category: '', skills: '' });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      category: item.category || '',
      skills: item.skills || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: editingId === 'new' ? undefined : editingId
      };
      await saveSkill(token, payload);
      setEditingId(null);
      onSave();
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this skill category?')) return;
    try {
      await deleteSkill(token, id);
      onSave();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading text-white">Skills & Tools</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#12d640] text-[#12d640] hover:bg-[#12d640]/10 font-mono text-sm"
        >
          <Plus size={16} /> Add Category
        </button>
      </div>

      {editingId && (
        <div className="glass-card p-4 space-y-3">
          <input
            placeholder="Category (e.g., Languages, Design & Simulation, Tools)"
            value={form.category}
            onChange={(e) => setForm({...form, category: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <textarea
            placeholder="Skills (comma separated)"
            value={form.skills}
            onChange={(e) => setForm({...form, skills: e.target.value})}
            className="w-full h-24 bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white resize-none text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.category || !form.skills}
              className="flex items-center gap-2 px-4 py-2 rounded text-black text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded border border-[#333] text-[#aaa] text-sm hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-4 flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium mb-2">{item.category}</h3>
              <div className="flex flex-wrap gap-1">
                {item.skills?.split(',').map((skill, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-[#12d640]/10 text-[#12d640] rounded">{skill.trim()}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-2 text-[#aaa] hover:text-[#12d640]">
                <Edit3 size={16} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-[#aaa] hover:text-red-500">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[#666] text-sm text-center py-8">No skill categories. Click "Add Category" to create one.</p>
        )}
      </div>
    </div>
  );
}

// Certifications Editor
function CertificationsEditor({ data, token, onSave }) {
  const [items, setItems] = useState(data || []);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', issuer: '', issue_date: '', credential_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleAdd = () => {
    setEditingId('new');
    setForm({ name: '', issuer: '', issue_date: '', credential_url: '' });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      issuer: item.issuer || '',
      issue_date: item.issue_date || '',
      credential_url: item.credential_url || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: editingId === 'new' ? undefined : editingId
      };
      await saveCertification(token, payload);
      setEditingId(null);
      onSave();
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification?')) return;
    try {
      await deleteCertification(token, id);
      onSave();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading text-white">Certifications</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#12d640] text-[#12d640] hover:bg-[#12d640]/10 font-mono text-sm"
        >
          <Plus size={16} /> Add
        </button>
      </div>

      {editingId && (
        <div className="glass-card p-4 space-y-3">
          <input
            placeholder="Certification Name *"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Issuing Organization *"
            value={form.issuer}
            onChange={(e) => setForm({...form, issuer: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Issue Date (e.g., 2023)"
            value={form.issue_date}
            onChange={(e) => setForm({...form, issue_date: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Credential URL (LinkedIn or verification link)"
            value={form.credential_url}
            onChange={(e) => setForm({...form, credential_url: e.target.value})}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.name || !form.issuer}
              className="flex items-center gap-2 px-4 py-2 rounded text-black text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded border border-[#333] text-[#aaa] text-sm hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.id} className="glass-card p-4 flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium truncate">{item.name}</h3>
              <p className="text-[#aaa] text-sm">{item.issuer}</p>
              <p className="text-[#666] text-xs">{item.issue_date}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-1.5 text-[#aaa] hover:text-[#12d640]">
                <Edit3 size={14} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-1.5 text-[#aaa] hover:text-red-500">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[#666] text-sm text-center py-8 col-span-2">No certifications. Click "Add" to create one.</p>
        )}
      </div>
    </div>
  );
}

// Main Admin Panel Component
export default function AdminPanel() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token
  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      verifyAdmin(savedToken)
        .then((result) => {
          if (result.valid) {
            setToken(savedToken);
          } else {
            localStorage.removeItem('adminToken');
          }
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
        });
    }
  }, []);

  // Load content and stats when authenticated
  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contentData, statsData] = await Promise.all([
        getAllContent(),
        getVisitorStats()
      ]);
      setContent(contentData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    navigate('/');
  };

  if (!token) {
    return <LoginForm onLogin={setToken} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#12d640]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <a href="/" className="font-heading text-xl font-semibold gradient-text">SK</a>
            <span className="text-[#666] hidden sm:inline">/</span>
            <span className="font-mono text-xs text-white hidden sm:inline">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#aaa] hover:text-white text-xs font-mono flex items-center gap-1"
            >
              <Eye size={14} /> View Site
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1.5 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-xs"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-[#333]">
          <TabButton active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} icon={BarChart3} label="Stats" />
          <TabButton active={activeTab === 'about'} onClick={() => setActiveTab('about')} icon={User} label="About" />
          <TabButton active={activeTab === 'education'} onClick={() => setActiveTab('education')} icon={GraduationCap} label="Education" />
          <TabButton active={activeTab === 'experience'} onClick={() => setActiveTab('experience')} icon={Briefcase} label="Experience" />
          <TabButton active={activeTab === 'projects'} onClick={() => setActiveTab('projects')} icon={FolderOpen} label="Projects" />
          <TabButton active={activeTab === 'skills'} onClick={() => setActiveTab('skills')} icon={Wrench} label="Skills" />
          <TabButton active={activeTab === 'certifications'} onClick={() => setActiveTab('certifications')} icon={Award} label="Certs" />
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#12d640]" size={40} />
          </div>
        ) : (
          <div>
            {activeTab === 'stats' && <StatsDashboard stats={stats} />}
            {activeTab === 'about' && <AboutEditor data={content?.about} token={token} onSave={loadData} />}
            {activeTab === 'education' && <EducationEditor data={content?.education} token={token} onSave={loadData} />}
            {activeTab === 'experience' && <ExperienceEditor data={content?.experience} token={token} onSave={loadData} />}
            {activeTab === 'projects' && <ProjectsEditor data={content?.projects} token={token} onSave={loadData} />}
            {activeTab === 'skills' && <SkillsEditor data={content?.skills} token={token} onSave={loadData} />}
            {activeTab === 'certifications' && <CertificationsEditor data={content?.certifications} token={token} onSave={loadData} />}
          </div>
        )}
      </main>
    </div>
  );
}
